'use client';

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/services/api';
import type {
  SupportChannel,
  SupportMessage,
  SupportStatus,
  SupportTicket,
} from '../services/support-api';

export type InboxFilter = 'awaiting_reply' | 'awaiting_user' | 'all_active' | 'resolved';

export interface AdminInboxTicket extends SupportTicket {
  tenant?: { id: string; name: string; cui: string | null } | null;
  user?: { id: string; name: string; email: string } | null;
  assignedTo?: { id: string; name: string; email: string } | null;
  _count?: { messages: number };
}

export interface AdminInboxTicketDetail extends AdminInboxTicket {
  messages: SupportMessage[];
}

const KEY = ['admin-support-inbox'] as const;

/**
 * Listare tichete admin pentru widget. Aducem o singura data toate cele active
 * + rezolvate (ultima saptamana) si filtram client-side — payload mic, mai
 * putin trafic API si filtrarea instantanee cand userul schimba pill-ul.
 */
export function useAdminInboxTickets(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, 'list'],
    queryFn: () =>
      api.get<{ data: AdminInboxTicket[] }>('/admin/support/tickets?limit=100'),
    enabled: opts.enabled ?? true,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });
}

/**
 * Numara tichetele care asteapta raspuns admin (open + pending_agent).
 * Folosit pentru badge-ul FAB. Deriva din cache-ul listei de mai sus, fara
 * fetch separat.
 */
export function useAdminInboxBadgeCount(): number {
  const { data } = useAdminInboxTickets();
  return useMemo(() => {
    if (!data?.data) return 0;
    return data.data.filter(
      (t) => t.status === 'open' || t.status === 'pending_agent',
    ).length;
  }, [data?.data]);
}

/**
 * Filtreaza tichetele in functie de pill-ul activ + sorteaza desc updatedAt
 * (cele mai noi primele).
 */
export function useFilteredInboxTickets(filter: InboxFilter): AdminInboxTicket[] {
  const { data } = useAdminInboxTickets();
  return useMemo(() => {
    if (!data?.data) return [];
    const filtered = data.data.filter((t) => {
      if (filter === 'awaiting_reply')
        return t.status === 'open' || t.status === 'pending_agent';
      if (filter === 'awaiting_user') return t.status === 'pending_user';
      if (filter === 'all_active')
        return t.status === 'open' || t.status === 'pending_agent' || t.status === 'pending_user';
      if (filter === 'resolved') return t.status === 'resolved' || t.status === 'closed';
      return true;
    });
    return filtered.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }, [data?.data, filter]);
}

/**
 * Detaliile unui tichet pentru thread-ul deschis. Polling rapid (10s) cat e
 * deschis ca admin sa vada raspunsul user-ului in timp aproape real.
 */
export function useAdminTicketDetail(ticketId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', ticketId],
    queryFn: () => api.get<AdminInboxTicketDetail>(`/admin/support/tickets/${ticketId}`),
    enabled: !!ticketId,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    staleTime: 2_000,
  });
}

/**
 * Trimite reply ca agent. Backend auto-tranziteaza statusul la `pending_user`
 * + emite email catre client + notifica restul super-adminilor.
 */
export function useAdminReply(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<SupportMessage>(`/admin/support/tickets/${ticketId}/messages`, { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', ticketId] });
      qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}

/**
 * Modifica statusul (Marchează rezolvat / Închide / Redeschide).
 */
export function useAdminUpdateStatus(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: SupportStatus) =>
      api.patch<SupportTicket>(`/admin/support/tickets/${ticketId}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', ticketId] });
      qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}

/**
 * Marcheaza notificarile pentru un anumit tichet ca citite (per admin curent).
 * Folosit cand admin deschide thread-ul — badge-ul scade instant.
 */
export function useMarkTicketSeen(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ count: number }>(`/admin/support/tickets/${ticketId}/mark-seen`, {}),
    onSuccess: () => {
      // Invalidam toate notificarile, ca sa scada badge-ul + sa se actualizeze
      // bell-ul din topbar cu acelasi count.
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export type { SupportChannel };
